import { lazy, type ComponentType } from 'react';

/**
 * Screens loaded on demand. The landing page is where most visitors arrive
 * and they never sign in, so nothing of the member area is in the entry
 * bundle. Each loader is kept so the same chunk can be prefetched on idle
 * once someone is signed in, and navigating then costs no download.
 */
type Loader = () => Promise<ComponentType>;

const pick =
  <M, K extends keyof M>(load: () => Promise<M>, key: K): Loader =>
  () =>
    load().then((m) => m[key] as ComponentType);

export const loaders = {
  AppLayout: pick(() => import('../AppLayout'), 'AppLayout'),
  Portal: pick(() => import('../pages/portal/Portal'), 'Portal'),
  CreateBill: pick(() => import('../pages/bills/CreateBill'), 'CreateBill'),
  BillsPage: pick(() => import('../pages/bills/BillsPage'), 'BillsPage'),
  StatsPage: pick(() => import('../pages/stats/StatsPage'), 'StatsPage'),
  MemberProfilePage: pick(() => import('../pages/members/MemberProfilePage'), 'MemberProfilePage'),
  Approvals: pick(() => import('../pages/approvals/Approvals'), 'Approvals'),
  SettingsTab: pick(() => import('../pages/settings/SettingsTab'), 'SettingsTab'),
  NotificationsPage: pick(() => import('../pages/notifications/NotificationsPage'), 'NotificationsPage'),
  Logs: pick(() => import('../pages/logs/Logs'), 'Logs'),
  /** The only screen pulling in Recharts (~150 kB gzipped). */
  PostsAdmin: pick(() => import('../pages/posts/PostsAdmin'), 'PostsAdmin'),
  PostEditorPage: pick(() => import('../pages/posts/PostEditorPage'), 'PostEditorPage'),
  MailPage: pick(() => import('../pages/mail/MailPage'), 'MailPage'),
  CampaignPage: pick(() => import('../pages/mail/CampaignPage'), 'CampaignPage'),
  GenerationsPage: pick(() => import('../pages/generations/GenerationsPage'), 'GenerationsPage'),
  HandoverWizard: pick(() => import('../pages/generations/HandoverWizard'), 'HandoverWizard'),
  EventsPage: pick(() => import('../pages/events/EventsPage'), 'EventsPage'),
  EventDetailPage: pick(() => import('../pages/events/EventDetailPage'), 'EventDetailPage'),
  /** Carries the QR encoder; nothing else loads it. */
  PresentPage: pick(() => import('../pages/events/PresentPage'), 'PresentPage'),
  CheckInPage: pick(() => import('../pages/events/CheckInPage'), 'CheckInPage'),
  TasksPage: pick(() => import('../pages/tasks/TasksPage'), 'TasksPage'),
  DocsPage: pick(() => import('../pages/docs/DocsPage'), 'DocsPage'),
  /** Renders Markdown (with GFM), like the news article. */
  DocPage: pick(() => import('../pages/docs/DocPage'), 'DocPage'),
  DocEditor: pick(() => import('../pages/docs/DocEditor'), 'DocEditor'),
  LoginScreen: pick(() => import('../pages/auth/LoginScreen'), 'LoginScreen'),
  /** Renders Markdown, so it carries the markdown chunk. */
  NewsDetailPage: pick(() => import('../public/NewsDetailPage'), 'NewsDetailPage'),
  CompetitionPage: pick(() => import('../public/CompetitionPage'), 'CompetitionPage'),
  JoinPage: pick(() => import('../public/JoinPage'), 'JoinPage'),
};

export type PageName = keyof typeof loaders;

export const pages = Object.fromEntries(
  Object.entries(loaders).map(([name, load]) => [name, lazy(() => load().then((c) => ({ default: c })))]),
) as Record<PageName, React.LazyExoticComponent<ComponentType>>;

/** What a signed-in member is likely to open next. Heavy admin-only screens
 *  (Dashboard with its charts) are left to load when opened. */
const MEMBER_SET: PageName[] = ['AppLayout', 'Portal', 'BillsPage', 'CreateBill', 'Approvals', 'NotificationsPage', 'SettingsTab', 'TasksPage', 'EventsPage', 'DocsPage', 'MemberProfilePage'];

let publicPrefetched = false;

/** The other public pages, fetched once the first one has settled, so a click
 *  in the menu never waits for a download. */
export function prefetchPublicPages(): void {
  if (publicPrefetched || typeof window === 'undefined') return;
  publicPrefetched = true;
  const run = () => (['CompetitionPage', 'JoinPage'] as PageName[]).forEach((name) => void loaders[name]().catch(() => undefined));
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  if (idle) idle(run, { timeout: 4000 });
  else setTimeout(run, 2000);
}

let prefetched = false;

export function prefetchMemberArea(): void {
  if (prefetched || typeof window === 'undefined') return;
  prefetched = true;
  const run = () => MEMBER_SET.forEach((name) => void loaders[name]().catch(() => undefined));
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  if (idle) idle(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}
